/* eslint-disable react/no-danger */
import React, { useCallback } from 'react';
import { Col, Row } from 'antd/lib/grid';
import { useForm } from 'antd/lib/form/Form';
import { Form, FORM_ITEM_RULES } from '@perxis-auto/forms';
import { Button } from '@perxis/perxis-auto-ui-kit/lib/components/Button';
import { Checkbox } from '@perxis/perxis-auto-ui-kit/lib/components/Checkbox';
import { Collapse } from '@perxis/perxis-auto-ui-kit/lib/components/Collapse';
import { FormItem } from '@perxis/perxis-auto-ui-kit/lib/components/FormItem';
import { Typography } from '@perxis/perxis-auto-ui-kit/lib/components/Typography';

import {
  sendRegisterPayment,
  TBookingInfoResponse,
  TBookingRequestResponse,
} from '@/api/payService';
import { ICarItem } from '@/types/car';
import config from '@/utils/config';
import { CUSTOM_RULES } from '@/utils/validateRules';
import { FORM_EVENTS } from '@/constants/events';
import { STATUS_MESSAGES } from '@/constants/common';
import { prettifyPhone, formatPrice } from '@/utils/common';
import { TClientData, TBookingAgreementData } from '@/apps/pre-order-form/types';
import { EVENTS_CHANNEL } from '@/apps/pre-order-form/constants/events';

import style from './style.module.scss';

type Props = {
  data?: TBookingRequestResponse;
  clientData?: TClientData;
  carData?: ICarItem;
  bookingAgreementData?: TBookingAgreementData;
  bookingInfo?: TBookingInfoResponse;
  bookingInfoError?: string;
};

export const ConfirmationFormFields: Record<keyof ConfirmationFormValues, string> = {
  confirm_contract_conditions: 'confirm_contract_conditions',
};

export type ConfirmationFormValues = {
  confirm_contract_conditions: boolean;
};

const initialValues = {
  confirm_contract_conditions: false,
};

const ConfirmationStep = ({
  data,
  carData,
  clientData,
  bookingInfo,
  bookingAgreementData,
  bookingInfoError,
}: Props) => {
  const [form] = useForm<ConfirmationFormValues>();

  // Скролл к заголовку при переходе ко второму шагу
  // Создан ref, к которому происходит скролл, чтобы заголовок
  // не скрылся за шапкой сайта. Его высота чуть больше высоты шапки,
  // чтобы заголовок не прилегал к ней вплотную.
  // Через useRef не работает при переходе по ссылке из письма, которое
  // приходит пользователю после первого шага
  const titleRef = useCallback((node: HTMLElement) => {
    node?.scrollIntoView({
      behavior: 'smooth',
    });
  }, []);

  const isHiddenStep =
    !bookingInfoError &&
    (!bookingAgreementData || ((!data || !carData || !clientData) && !bookingInfo));

  if (isHiddenStep) {
    return null;
  }

  const onFinish = async (values: any) => {
    try {
      const { data: paymentData } = await sendRegisterPayment({
        ...values,
        booking_request_id: data?.booking_request_id ?? bookingInfo?.booking_request_id,
      });

      const { form_url: UrlToRedirect } = paymentData;
      if (UrlToRedirect) {
        document.location = UrlToRedirect;
      }
    } catch (error) {
      const failureRedirectUrl = config.get('failureRedirectUrl');
      document.location = failureRedirectUrl;
    }
  };

  const error = data?.error_info ?? bookingInfoError;
  const isError = !!error;
  const model = carData?.data?.model?.displayName ?? bookingInfo?.vehicle_model;
  const color = carData?.data?.exterior?.displayName ?? bookingInfo?.vehicle_exterior;
  const brand =
    carData?.data?.brand?.displayName ?? bookingInfo?.vehicle_brand ?? 'Omoda';
  const firstName = clientData?.first_name ?? bookingInfo?.client_firstname;
  const lastName = clientData?.last_name ?? bookingInfo?.client_lastname;
  const patronymic = clientData?.patronymic ?? bookingInfo?.client_patronymic;
  const region = clientData?.region ?? bookingInfo?.client_region;
  const dealership = clientData?.dealership ?? bookingInfo?.dealership;
  const dealershipAddress =
    clientData?.dealership_address ?? bookingInfo?.dealership_address;
  const phone = prettifyPhone(clientData?.phone ?? bookingInfo?.client_phone);
  const email = clientData?.email ?? bookingInfo?.client_email;
  const fullName = [lastName, firstName, patronymic].join(' ');
  const carName = model ? [brand, model].join(' ') : '';
  const version = carData?.data?.version?.displayName ?? bookingInfo?.vehicle_version;

  return !isError ? (
    <Row gutter={[0, { xs: 36, sm: 36, lg: 40, xl: 48 }]}>
      <Col span={24}>
        <Typography.Title className={style.title} level={3} classLevel={1}>
          <span ref={titleRef} className={style.titleAnchor}></span>
          Предоплата автомобиля
        </Typography.Title>
        <div className={style.descriptionWrapper}>
          <Typography.Text className={style.description}>
            Для завершения бронирования необходимо внести предоплату в 10&nbsp;000&nbsp;₽.
            <br />
            Бронирование доступно в течение двух часов.
          </Typography.Text>
        </div>
      </Col>
      <Col span={24}>
        <Row className={style.info} gutter={[{ xs: 0, md: 40 }, 0]}>
          <Col xs={24} md={12}>
            {!!fullName && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  ФИО
                </Col>
                <Col span={12} className={style.infoValue}>
                  {fullName}
                </Col>
              </Row>
            )}
            {!!region && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  Город
                </Col>
                <Col span={12} className={style.infoValue}>
                  {region}
                </Col>
              </Row>
            )}
            <Row className={style.infoRow}>
              <Col span={12} className={style.infoTitle}>
                Дилерский центр
              </Col>
              <Col span={12} className={style.infoValue}>
                {dealership
                  ? `${dealership}${dealershipAddress ? `, ${dealershipAddress}` : ''}`
                  : 'По\u00A0согласованию сторон'}
              </Col>
            </Row>
            {!!phone && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  Телефон
                </Col>
                <Col span={12} className={style.infoValue}>
                  {phone}
                </Col>
              </Row>
            )}
            {!!email && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  E-mail
                </Col>
                <Col span={12} className={style.infoValue}>
                  {email}
                </Col>
              </Row>
            )}
          </Col>
          <Col xs={24} md={12}>
            {!!carName && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  Автомобиль
                </Col>
                <Col span={12} className={style.infoValue}>
                  {carName}
                </Col>
              </Row>
            )}
            {!!version && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  Комплектация
                </Col>
                <Col span={12} className={style.infoValue}>
                  {version}
                </Col>
              </Row>
            )}
            {!!color && (
              <Row className={style.infoRow}>
                <Col span={12} className={style.infoTitle}>
                  Цвет
                </Col>
                <Col span={12} className={style.infoValue}>
                  {color}
                </Col>
              </Row>
            )}
            <Row className={style.infoRow}>
              <Col span={12} className={style.infoTitle}>
                Сумма предоплаты
              </Col>
              <Col span={12} className={style.infoValue}>
                {bookingInfo?.prepayment_amount
                  ? formatPrice(bookingInfo.prepayment_amount)
                  : '10\u00A0000\u00A0₽'}
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <Collapse className={style.collapse} defaultActiveKey="1">
          <Collapse.Panel
            header="Договор резервирования (бронирования) автомобиля"
            key="1"
          >
            <div
              dangerouslySetInnerHTML={{ __html: bookingAgreementData?.html ?? '' }}
              className={style.bookingAgreement}
            />
          </Collapse.Panel>
        </Collapse>
      </Col>
      {/* TODO: Временно скрыто 
      <Col span={24}>
        <Typography.Text className={style.mark}>
          На ваш e-mail отправлено письмо со ссылкой на оплату, 
          которая действительна в течение двух часов.
        </Typography.Text>
      </Col> */}
      <Col span={24}>
        <Form<ConfirmationFormValues>
          form={form}
          formEvents={FORM_EVENTS}
          formChannel={EVENTS_CHANNEL}
          initialValues={initialValues}
          onFinish={onFinish}
          className={style.omodaConfirmationForm}
          defaultMessages={STATUS_MESSAGES}
        >
          {({ isSubmitting, status }) => {
            const showErrorMessage = status?.type === 'error' && !!status.message;
            return (
              <>
                <Row justify="space-between" gutter={[32, 18]}>
                  <Col xs={24} md={12}>
                    <FormItem
                      className={style.checkboxField}
                      name={ConfirmationFormFields.confirm_contract_conditions}
                      rules={[FORM_ITEM_RULES.required, CUSTOM_RULES.offer]}
                      valuePropName="checked"
                    >
                      <Checkbox>
                        Ознакомлен и принимаю условия Договора бронирования
                      </Checkbox>
                    </FormItem>
                  </Col>
                  <Col xs={24} md={12}>
                    <Row justify="end" gutter={[32, 18]}>
                      <Col className={style.buttonCol}>
                        <Button
                          type="ghost"
                          htmlType="submit"
                          disabled={isSubmitting}
                          className={style.submitBtn}
                        >
                          Оплатить
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                {showErrorMessage && (
                  <Typography.Paragraph className={style.errorMessage}>
                    {status.message}
                  </Typography.Paragraph>
                )}
              </>
            );
          }}
        </Form>
      </Col>
    </Row>
  ) : (
    <Row className={style.errorContainer}>
      <Col span={24}>
        <Typography.Title level={5} className={style.errorMessage}>
          {error}
        </Typography.Title>
      </Col>
    </Row>
  );
};

export default ConfirmationStep;
